import clsx from "clsx";
import React, { PropsWithChildren, ReactNode, useRef, useState } from "react";
import {
  Tooltip as BootstrapTooltip,
  OverlayTrigger,
  Popover
} from "react-bootstrap";

import TooltipProps from "./@types/TooltipProps";
import styles from "./Tooltip.module.scss";
import { INTERACTIVE_TOOLTIP_MOUSEOUT_DELAY } from "./consts";

const Tooltip: React.FC<PropsWithChildren<TooltipProps>> = ({
  children,
  content,
  variant = "basic",
  trigger = ["hover", "focus"],
  size = "regular",
  useDefaultTargetStyle = false,
  placement = "bottom",
  id,
  className = "",
  innerClassName = "",
  header,
  disabled = false,
  onToggle,
  "data-testid": dataTestId,
  popperConfig
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const showTimeoutRef = useRef<NodeJS.Timeout>();
  const hideTimeoutRef = useRef<NodeJS.Timeout>();

  const hasClickTrigger = trigger === "click";
  const isInteractive = variant === "interactive";

  const isHoverInteractive =
    isInteractive &&
    (trigger === "hover" ||
      (Array.isArray(trigger) && trigger.includes("hover")));

  const handleMouseEnter = () => {
    if (!isHoverInteractive) return;

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    showTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, INTERACTIVE_TOOLTIP_MOUSEOUT_DELAY);
  };

  const handleMouseLeave = () => {
    if (!isHoverInteractive) return;

    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, INTERACTIVE_TOOLTIP_MOUSEOUT_DELAY);
  };

  const handleOnToggle = (nextShow: boolean) => {
    onToggle?.(nextShow);
    if (hasClickTrigger || !isHoverInteractive) {
      setShowTooltip(nextShow);
    }
  };

  const targetClassName =
    clsx({
      [styles["target-default-styling"]]: useDefaultTargetStyle,
      [styles["tooltip-trigger-variant-click"]]: hasClickTrigger
    }) || undefined;

  const isTooltipContentEmpty = (value: ReactNode): boolean => {
    if (value == null || value === "") return true;

    if (React.isValidElement(value) && value.type === React.Fragment) {
      return !value.props.children;
    }

    return false;
  };

  const renderContent = (props: any) => {
    const commonProps = {
      ...props,
      className: clsx(
        styles.tooltipComponent,
        styles[`tooltip-size-${size}`],
        { [styles.interactive]: isInteractive },
        className
      ),
      id,
      "data-testid": dataTestId,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    };

    if (variant === "basic") {
      return <BootstrapTooltip {...commonProps}>{content}</BootstrapTooltip>;
    }

    return (
      <Popover {...commonProps}>
        {header && <Popover.Header>{header}</Popover.Header>}
        <Popover.Body>{content}</Popover.Body>
      </Popover>
    );
  };

  if (disabled || isTooltipContentEmpty(content)) {
    return <>{children}</>;
  }

  return (
    <OverlayTrigger
      trigger={trigger}
      placement={placement}
      overlay={renderContent}
      onToggle={handleOnToggle}
      show={isHoverInteractive ? showTooltip : undefined}
      popperConfig={popperConfig}
      rootClose={hasClickTrigger}
      delay={{ show: 200, hide: 0 }}
    >
      <span
        className={clsx(targetClassName, innerClassName)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>
    </OverlayTrigger>
  );
};

export default Tooltip;
